'use client'

import { buildJournalArticleUrl } from 'src/_helpers/journals/buildJournalArticleUrl'
import Typography from 'src/components/design-system/Typography'
import { JournalArticleItem } from 'src/components/items/JournalArticleItem'
import { useTranslateEntry } from 'src/i18n/client/useTranslateEntry'
import useJournalDomainRoutes from 'src/components/journals/domain/useJournalDomainRoutes'
import { getPreferredTranslation } from 'src/_helpers/getPreferredTranslation'

const formatDate = (rawDate) => {
  if (!rawDate) return null

  const date = new Date(rawDate)
  if (!Number.isNaN(date.getTime())) {
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = date.getFullYear()
    return `${day}.${month}.${year}`
  }

  if (typeof rawDate === 'string') {
    const normalized = rawDate.replace(/\//g, '.').trim()
    if (/^\d{2}\.\d{2}\.\d{4}$/.test(normalized)) {
      return normalized
    }
    return normalized
  }

  return null
}

export default function OftenRead({ block, locale, variant }) {
  const translate = useTranslateEntry()
  const journalRoutes = useJournalDomainRoutes()
  const isDomainJournal = variant === 'journal-domain'

  const articles = Array.isArray(block.articles) ? block.articles : []
  const blockTranslation = getPreferredTranslation(block, locale) || {}
  const blockTitle = blockTranslation?.title?.trim()

  if (!articles.length) return null

  return (
    <section className={`my-64 d:px-40 px-16 ${block.type.value}`} id={block.uuid}>
      {blockTitle && (
        <Typography variant="h2-caps-bold" color="text-basic-black" className="pb-32">
          {blockTitle}
        </Typography>
      )}

      <ul
        className={`grid gap-x-42 gap-y-24 t:grid-cols-2 d:grid-cols-4 border-y-1 py-16 ${
          isDomainJournal ? 'border-y-basic-gray8' : 'border-y-dark-blue-dark-blue6'
        }`}
      >
        {articles.map((item, i) => {
          const article = item

          const articleTranslation = getPreferredTranslation(article, locale) || {}
          const articleTitle = articleTranslation?.title || ''

          const date = formatDate(article?.publishedAt)

          const url =
            buildJournalArticleUrl(
              article,
              locale,
              variant === 'journal-domain' ? 'journal-domain' : 'main',
              journalRoutes,
            ) || '#'

          const tags = Array.isArray(articleTranslation?.tags)
            ? articleTranslation.tags.map((label) => {
                const normalized = String(label).toLowerCase()
                const type = normalized.includes('nowość') || normalized.includes('nowosc') ? 'new' : 'default'
                return { label, type }
              })
            : []

          const authors = (article.author || []).map((a) => a?.fullName).filter(Boolean)

          return (
            <li
              key={i}
              className={`relative after:h-full after:w-1 ${
                isDomainJournal ? 'after:bg-basic-gray8' : 'after:bg-dark-blue-dark-blue6'
              } after:absolute after:top-0 after:-right-21 after:hidden d:after:block d:last:after:hidden`}
            >
              <JournalArticleItem
                date={date}
                title={articleTitle}
                url={url}
                tags={[
                  article.novelty && { label: translate('T_NOVELTY'), type: 'new' },
                  //article.openAccess && { label: translate('T_OPEN_ACCESS'), type: '' },
                  ...tags,
                ].filter(Boolean)}
                author={authors.join(', ')}
                variant={isDomainJournal ? 'journal-domain' : 'default'}
              />
            </li>
          )
        })}
      </ul>
    </section>
  )
}
